import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevLeftIcon, MenuIcon } from '../icons'
import { cn, cva } from '../lib/cva'
import { Text } from './Text'
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip'

const TOOLTIP_DELAY_MS = 500

export const SIDEBAR_COLLAPSED_STORAGE_KEY = 'viapeople:sidebar-collapsed'

export const readPersistedSidebarCollapsed = (fallback: boolean): boolean => {
	if (globalThis.window === undefined) return fallback
	const stored = globalThis.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY)
	if (stored === 'true') return true
	if (stored === 'false') return false
	return fallback
}

export const SIDEBAR_CONSTANTS = {
	ANIMATION_DELAY: 150,
	ICON_SIZE: 20,
	COLLAPSED_WIDTH: 60,
}

export interface IconProps {
	className?: string
}

export interface MenuItem {
	id: string
	label: string
	icon: React.FC<IconProps>
	collapsedIcon?: React.FC<IconProps>
	href?: string
	onClick?: () => void
	/**
	 * When > 0, renders a red notification pill next to the menu item label
	 * (and as a small dot in the top-right of the icon when the sidebar is
	 * collapsed). Falsy values render nothing.
	 */
	badge?: number
}

export interface SidebarProps {
	logoSrc?: string
	menuItems?: MenuItem[]
	defaultCollapsed?: boolean
	footerContent?: React.ReactNode
	bottomActions?: MenuItem[]
	onCollapsedChange?: (collapsed: boolean) => void
}

const sidebarVariants = cva(
	'flex flex-col fixed left-0 top-0 h-dvh bg-neutral-0 border-r border-neutral-200 transition-all duration-300 z-100 overflow-hidden shadow-lg',
	{
		variants: {
			collapsed: {
				true: 'w-15',
				false: 'w-78',
			},
		},
		defaultVariants: {
			collapsed: false,
		},
	},
)

const menuItemContainerVariants = cva('w-full my-0.5 hover:bg-neutral-100 rounded-lg', {
	variants: {
		collapsed: {
			true: 'mx-1',
			false: 'mx-2',
		},
		selected: {
			true: 'bg-neutral-100',
			false: '',
		},
	},
	defaultVariants: {
		collapsed: false,
		selected: false,
	},
})

const textVariants = cva('truncate whitespace-nowrap transition-opacity duration-150', {
	variants: {
		visible: {
			true: 'opacity-100',
			false: 'opacity-0',
		},
		selected: {
			true: 'text-primary-600',
			false: '',
		},
	},
	defaultVariants: {
		visible: true,
		selected: false,
	},
})

const iconVariants = cva('', {
	variants: {
		selected: {
			true: 'fill-primary-600',
			false: '',
		},
	},
	defaultVariants: {
		selected: false,
	},
})

const toggleButtonVariants = cva('cursor-pointer', {
	variants: {
		collapsed: {
			true: 'flex ml-1 w-full text-neutral-800',
			false: 'flex items-center text-neutral-500 whitespace-nowrap overflow-hidden',
		},
	},
	defaultVariants: {
		collapsed: false,
	},
})

const footerContainerVariants = cva('py-4 px-3.5 mb-4 w-full transition-all duration-300', {
	variants: {
		collapsed: {
			true: 'ml-1.5',
			false: '',
		},
	},
	defaultVariants: {
		collapsed: false,
	},
})

interface NavItemProps {
	item: MenuItem
	collapsed: boolean
	textVisible: boolean
	isSelected: boolean
	iconLeftPadding: number
	iconSize: number
}

const NavItem: React.FC<NavItemProps> = ({ item, collapsed, textVisible, isSelected, iconLeftPadding, iconSize }) => {
	const isExternalLink = item.href?.startsWith('http')
	const hasBadge = typeof item.badge === 'number' && item.badge > 0

	const content = (
		<div className={menuItemContainerVariants({ collapsed, selected: isSelected })}>
			<div
				className='absolute top-0 flex items-center justify-center h-full'
				style={{
					left: `${iconLeftPadding}px`,
					width: `${iconSize}px`,
				}}>
				{item.icon &&
					(collapsed && item.collapsedIcon ? (
						<item.collapsedIcon className={cn(iconVariants({ selected: isSelected }))} />
					) : (
						<item.icon className={cn(iconVariants({ selected: isSelected }))} />
					))}
				{hasBadge &&
					collapsed &&
					(() => {
						// hasBadge already guarantees item.badge is a number > 0.
						// Share the capped display value between the visible text and
						// the aria-label so screen readers don't announce "150 pending"
						// while sighted users see "99+".
						const badge = item.badge as number
						const display = badge > 99 ? '99+' : String(badge)
						return (
							<output
								aria-label={`${display} pending`}
								className='absolute -top-0.5 -right-4 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-error-600 text-neutral-0 text-[9px] font-semibold leading-none ring-2 ring-neutral-0'>
								{display}
							</output>
						)
					})()}
			</div>

			{!collapsed && (
				<div className='pl-11 pr-3 flex items-center justify-between h-full overflow-hidden gap-2'>
					<Text
						variant='xsmall'
						weight='semibold'
						className={textVariants({
							visible: textVisible,
							selected: isSelected,
						})}>
						{item.label}
					</Text>
					{hasBadge && (
						<output
							aria-label={`${item.badge} pending`}
							className='inline-flex shrink-0 items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-error-600 text-neutral-0 text-[10px] font-semibold leading-none'>
							{item.badge}
						</output>
					)}
				</div>
			)}
		</div>
	)

	const link = isExternalLink ? (
		<a
			href={item.href}
			onClick={item.onClick}
			className='flex w-full relative h-13.5 text-neutral-800'
			target='_blank'
			rel='noopener noreferrer'>
			{content}
		</a>
	) : (
		<Link to={item.href || '#'} onClick={item.onClick} className='flex w-full relative h-13.5 text-neutral-800'>
			{content}
		</Link>
	)

	if (!collapsed) {
		return link
	}

	return (
		<Tooltip delayDuration={TOOLTIP_DELAY_MS}>
			<TooltipTrigger asChild>{link}</TooltipTrigger>
			<TooltipContent side='right'>
				{item.label}
				{hasBadge && ` (${item.badge})`}
			</TooltipContent>
		</Tooltip>
	)
}

const useActiveMenuItem = (menuItems: MenuItem[]) => {
	const location = useLocation()
	const activeItem = React.useMemo(() => {
		const pathname = location.pathname
		const reversed = pathname.split('/').reverse()
		const matchingItem = [...menuItems].reverse().find((item) => {
			return reversed.find((path) => path === item.id)
		})
		return matchingItem?.id || menuItems[0]?.id
	}, [location.pathname, menuItems])

	return activeItem
}

export const Sidebar: React.FC<SidebarProps> = ({
	logoSrc,
	menuItems = [],
	defaultCollapsed = true,
	footerContent,
	bottomActions,
	onCollapsedChange,
}) => {
	const [collapsed, setCollapsed] = useState(() => readPersistedSidebarCollapsed(defaultCollapsed))
	const [textVisible, setTextVisible] = useState(() => !readPersistedSidebarCollapsed(defaultCollapsed))
	const activeMenuItem = useActiveMenuItem(menuItems)

	useEffect(() => {
		onCollapsedChange?.(collapsed)
		if (globalThis.window !== undefined) {
			globalThis.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed))
		}
	}, [collapsed, onCollapsedChange])

	const toggleCollapse = () => {
		if (collapsed) {
			setCollapsed(false)
			setTimeout(() => setTextVisible(true), SIDEBAR_CONSTANTS.ANIMATION_DELAY)
		} else {
			setTextVisible(false)
			setTimeout(() => setCollapsed(true), SIDEBAR_CONSTANTS.ANIMATION_DELAY)
		}
	}

	const iconLeftPadding = (SIDEBAR_CONSTANTS.COLLAPSED_WIDTH - SIDEBAR_CONSTANTS.ICON_SIZE) / 2

	// Calculate effective bottom actions (support both old bottomAction and new bottomActions)
	const effectiveBottomActions = React.useMemo(() => {
		if (bottomActions && bottomActions.length > 0) {
			return bottomActions
		}
		return []
	}, [bottomActions])

	const toggleButton = (
		<button
			type='button'
			onClick={toggleCollapse}
			className={toggleButtonVariants({ collapsed })}
			aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}>
			{collapsed ? (
				<MenuIcon />
			) : (
				<>
					<ChevLeftIcon className='ml-1 mr-3 flex-shrink-0' />
					<Text variant='xsmall' className={textVariants({ visible: textVisible })}>
						Collapse menu
					</Text>
				</>
			)}
		</button>
	)

	return (
		<div className={sidebarVariants({ collapsed })}>
			<div className='p-4 mt-3 mb-2'>
				{collapsed ? (
					<Tooltip delayDuration={TOOLTIP_DELAY_MS}>
						<TooltipTrigger asChild>{toggleButton}</TooltipTrigger>
						<TooltipContent side='right'>Expand menu</TooltipContent>
					</Tooltip>
				) : (
					toggleButton
				)}
			</div>

			<nav className='flex-1 overflow-y-auto overflow-x-hidden'>
				<ul className='flex flex-col'>
					{menuItems.map((item) => (
						<li key={item.id} className='w-full'>
							<NavItem
								item={item}
								collapsed={collapsed}
								textVisible={textVisible}
								isSelected={activeMenuItem === item.id}
								iconLeftPadding={iconLeftPadding}
								iconSize={SIDEBAR_CONSTANTS.ICON_SIZE}
							/>
						</li>
					))}
				</ul>
			</nav>

			{effectiveBottomActions.length > 0 && (
				<div className='w-full mt-auto'>
					{effectiveBottomActions.map((action, index) => (
						<div key={action.id} className={`w-full ${index === effectiveBottomActions.length - 1 ? 'mb-1' : ''}`}>
							<NavItem
								item={action}
								collapsed={collapsed}
								textVisible={textVisible}
								isSelected={activeMenuItem === action.id}
								iconLeftPadding={iconLeftPadding}
								iconSize={SIDEBAR_CONSTANTS.ICON_SIZE}
							/>
						</div>
					))}
				</div>
			)}

			<footer className='w-full overflow-hidden'>
				<div className='border-t border-neutral-200 w-full' />
				<div className={footerContainerVariants({ collapsed: !collapsed })}>
					{footerContent ? footerContent : logoSrc && <img src={logoSrc} alt='Logo' className='h-15 object-contain' />}
				</div>
			</footer>
		</div>
	)
}
